import { createTheme } from '@mui/material/styles';
import '@fontsource/heebo/400.css';
import '@fontsource/heebo/500.css';
import '@fontsource/heebo/700.css';
import '@fontsource/heebo/800.css';

/**
 * Dashboard theme (aligned with the admin panel design system, direction-aware for RTL).
 * Heebo carries both Hebrew and Latin text; Inter stays as fallback.
 */
export const buildTheme = (direction = 'rtl') =>
  createTheme({
    direction,
    palette: {
      primary: {
        main: '#6D28D9',
        light: '#8B5CF6',
        dark: '#5B21B6',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#9333EA',
        light: '#A855F7',
        dark: '#7C3AED',
      },
      background: {
        default: '#F8FAFC',
        paper: '#FFFFFF',
      },
      error:   { main: '#EF4444' },
      success: { main: '#10B981' },
      warning: { main: '#F59E0B' },
      info:    { main: '#3B82F6' },
      text: {
        primary:   '#111827',
        secondary: '#6B7280',
        disabled:  '#9CA3AF',
      },
      divider: '#E5E7EB',
    },
    typography: {
      fontFamily: '"Heebo", -apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      h4: { fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 500, textTransform: 'none' },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: '#D1D5DB transparent',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: '#D1D5DB', borderRadius: 3 },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)',
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          contained: {
            '&:hover': { boxShadow: '0 2px 8px rgba(109, 40, 217, 0.35)' },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            backgroundColor: '#F9FAFB',
            color: '#6B7280',
            borderBottom: '1px solid #E5E7EB',
          },
          body: {
            borderBottom: '1px solid #F3F4F6',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 14 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: 12 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 4 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
    },
  });
